import { useEffect, useMemo, useState } from 'react';
import { AlertIcon, BasketIcon, CheckIcon, CopyIcon, UtensilsIcon } from '../components/Icons';
import { getPublicOrder, readFragmentToken } from '../lib/api';
import { buildDailyWechatText, buildWechatText } from '../lib/calculation';
import { formatShortDate, formatWeekRange, weekdayLabel } from '../lib/date';
import { groupPurchaseItemsByCategory, purchaseDisplayCategory } from '../lib/ingredient-categories';
import type { PublicOrder, PurchaseItem, WechatMessageKind, WechatPurchaseMessage } from '../types';

const WECHAT_MESSAGE_LABELS: Record<WechatMessageKind, string> = {
  meat: '荤菜',
  vegetable: '素菜',
  condiment: '其他采购',
};

function belongsToWechatMessage(item: PurchaseItem, kind: WechatMessageKind): boolean {
  const category = purchaseDisplayCategory(item.ingredientCategory, item.name);
  if (kind === 'meat') return category === '荤菜';
  if (kind === 'vegetable') return category === '素菜';
  return category === '其他采购';
}

function wechatMessageItemCount(order: PublicOrder, kind: WechatMessageKind): number {
  const hasGroupedItems = order.dailyPurchaseGroups !== undefined
    || order.extraPurchaseItems !== undefined;
  const messageItems = hasGroupedItems
    ? [
        ...(order.dailyPurchaseGroups ?? []).flatMap((group) => group.items),
        ...(order.extraPurchaseItems ?? []),
      ]
    : order.purchaseItems;
  return messageItems.filter((item) => belongsToWechatMessage(item, kind)).length;
}

function wechatMessageDescription(kind: WechatMessageKind, itemCount: number): string {
  if (itemCount === 0) {
    return '本周无采购，可复制后直接发送';
  }
  if (kind === 'condiment') return `${itemCount} 项 · 含主食干货、调味品和其他 · 按天展示`;
  return `${itemCount} 项 · 按天展示`;
}

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const element = document.createElement('textarea');
  element.value = text;
  element.style.position = 'fixed';
  element.style.opacity = '0';
  document.body.appendChild(element);
  element.select();
  document.execCommand('copy');
  element.remove();
}

export function PublicOrderPage() {
  const token = useMemo(() => readFragmentToken('share'), []);
  const [order, setOrder] = useState<PublicOrder | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState<WechatMessageKind | 'legacy' | null>(null);

  useEffect(() => {
    if (!token) {
      setError('分享链接不完整，请联系厨房负责人重新发送。');
      return;
    }
    getPublicOrder(token).then(setOrder).catch((reason) => setError(reason instanceof Error ? reason.message : '采购单加载失败'));
  }, [token]);

  const copy = async (message?: WechatPurchaseMessage) => {
    if (!order) return;
    const text = message?.text ?? (
      order.wechatText || (order.dailyPurchaseGroups !== undefined || order.extraPurchaseItems !== undefined
        ? buildDailyWechatText(
            order.kitchenName,
            formatWeekRange(order.weekStart, order.weekEnd),
            order.dailyPurchaseGroups ?? [],
            order.extraPurchaseItems ?? [],
          )
        : buildWechatText(order.kitchenName, formatWeekRange(order.weekStart, order.weekEnd), order.purchaseItems))
    );
    await copyText(text);
    setCopied(message?.kind ?? 'legacy');
    window.setTimeout(() => setCopied(null), 2200);
  };

  return (
    <main className="public-order-page">
      <nav className="public-order-nav shell--narrow">
        <span className="brand"><span className="brand__mark"><UtensilsIcon size={21} /></span><span><strong>龙庭备菜助手</strong><small>采购单只读页面</small></span></span>
        <span className="read-only-badge"><CheckIcon size={14} /> 只读</span>
      </nav>
      {!order && !error && <div className="page-loading"><span className="spinner" /><p>正在打开采购单…</p></div>}
      {error && (
        <section className="access-error shell--narrow"><span><AlertIcon size={30} /></span><h1>无法打开采购单</h1><p>{error}</p><small>出于数据安全考虑，失效链接无法恢复。</small></section>
      )}
      {order && (
        <section className="public-order shell--narrow">
          <header className="public-order__header">
            <span className="public-order__icon"><BasketIcon size={27} /></span>
            <p className="eyebrow">PURCHASE LIST · V{order.version}</p>
            <h1>{order.kitchenName}<br />备菜采购清单</h1>
            <div className="public-order__week">{formatWeekRange(order.weekStart, order.weekEnd)}</div>
          </header>
          {order.dailyPurchaseGroups !== undefined || order.extraPurchaseItems !== undefined ? (
            <div className="public-order__groups">
              {(order.dailyPurchaseGroups ?? []).map((group) => (
                <section className="public-order__group" key={group.date}>
                  <header><strong>{formatShortDate(group.date)} {weekdayLabel(group.date)}</strong><span>当日汇总</span></header>
                  <div className="public-order__categories">
                    {groupPurchaseItemsByCategory(group.items).map((categoryGroup) => (
                      <section className="public-order__category" key={categoryGroup.category}>
                        <h3>{categoryGroup.category}<small>{categoryGroup.items.length} 项</small></h3>
                        <div className="public-order__list">
                          {categoryGroup.items.map((item, index) => (
                            <div className="public-order__row" key={item.key}>
                              <span>{String(index + 1).padStart(2, '0')}</span><strong>{item.name}</strong><b>{item.finalQuantity}<small>{item.unit}</small></b>
                            </div>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </section>
              ))}
              {(order.extraPurchaseItems?.length ?? 0) > 0 && (
                <section className="public-order__group public-order__group--extra">
                  <header><strong>本周额外采购</strong><span>固定采购与临时补货</span></header>
                  <div className="public-order__categories">
                    {groupPurchaseItemsByCategory(order.extraPurchaseItems ?? []).map((categoryGroup) => (
                      <section className="public-order__category" key={categoryGroup.category}>
                        <h3>{categoryGroup.category}<small>{categoryGroup.items.length} 项</small></h3>
                        <div className="public-order__list">
                          {categoryGroup.items.map((item, index) => (
                            <div className="public-order__row" key={item.key}>
                              <span>{String(index + 1).padStart(2, '0')}</span><strong>{item.name}</strong><b>{item.finalQuantity}<small>{item.unit}</small></b>
                            </div>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </section>
              )}
            </div>
          ) : (
            <section className="public-order__group public-order__group--legacy">
              <header><strong>整周汇总</strong><span>历史版本</span></header>
              <div className="public-order__list">
                {order.purchaseItems.map((item, index) => (
                  <div className="public-order__row" key={item.key}>
                    <span>{String(index + 1).padStart(2, '0')}</span><strong>{item.name}</strong><b>{item.finalQuantity}<small>{item.unit}</small></b>
                  </div>
                ))}
              </div>
            </section>
          )}
          {order.wechatMessages !== undefined ? (
            <section className="public-order__message-actions" aria-label="微信采购消息">
              <header>
                <strong>分三条发送采购消息</strong>
                <small>荤菜、素菜和其他采购分别复制，均按天展示</small>
              </header>
              {order.wechatMessages.map((message) => {
                const itemCount = wechatMessageItemCount(order, message.kind);
                const isCopied = copied === message.kind;
                return (
                  <button
                    className={`public-order__message-copy public-order__message-copy--${message.kind}`}
                    type="button"
                    key={message.kind}
                    onClick={() => copy(message)}
                    aria-label={isCopied ? `${WECHAT_MESSAGE_LABELS[message.kind]}消息已复制` : `复制${WECHAT_MESSAGE_LABELS[message.kind]}消息`}
                  >
                    {isCopied ? <CheckIcon /> : <CopyIcon />}
                    <span>
                      <strong>{isCopied ? `${WECHAT_MESSAGE_LABELS[message.kind]}消息已复制` : `复制${WECHAT_MESSAGE_LABELS[message.kind]}消息`}</strong>
                      <small>{wechatMessageDescription(message.kind, itemCount)}</small>
                    </span>
                    <em className={itemCount === 0 ? 'is-empty' : ''}>{itemCount === 0 ? '无采购' : `${itemCount} 项`}</em>
                  </button>
                );
              })}
            </section>
          ) : (
            <button className="button button--primary public-order__copy" type="button" onClick={() => copy()}>
              {copied === 'legacy' ? <><CheckIcon /> 历史文案已复制</> : <><CopyIcon /> 复制历史微信文案</>}
            </button>
          )}
          <p className="public-order__footnote">本页面由厨房负责人确认生成，仅用于查看采购数量。</p>
        </section>
      )}
    </main>
  );
}
